import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { AuthShell } from "./login";
import { PLANS, PLAN_ORDER, type PlanKey, type BillingInterval } from "@/lib/billing-plans";
import { createSignupCheckoutSession } from "@/lib/billing.functions";
import { useServerFn } from "@tanstack/react-start";
import { cn } from "@/lib/utils";
import { Check, CreditCard } from "lucide-react";
import { redirectIfAuthenticated } from "@/lib/marketing-guards";

const SignupSearch = z.object({
  plan: z.enum(["starter", "pro", "super"]).optional(),
  interval: z.enum(["monthly", "annual"]).optional(),
  cancelled: z.coerce.boolean().optional(),
});


export const Route = createFileRoute("/signup")({
  beforeLoad: redirectIfAuthenticated,
  validateSearch: (s) => SignupSearch.parse(s),
  head: () => ({
    meta: [
      { title: "Start your free trial, Super Affiliate OS" },
      {
        name: "description",
        content: "Create your Super Affiliate OS account and start your 14-day free trial.",
      },
    ],
  }),
  component: SignupPage,
});

function SignupPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const startCheckout = useServerFn(createSignupCheckoutSession);
  const search = useSearch({ from: "/signup" });

  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState<PlanKey>(search.plan ?? "pro");
  const [interval, setInterval] = useState<BillingInterval>(search.interval ?? "monthly");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate({ to: "/dashboard" });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (search.cancelled) {
      toast.info("Checkout cancelled, no charge was made.");
    }
  }, [search.cancelled]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const origin = window.location.origin;
      const res = await startCheckout({ data: { email, plan, interval, origin } });
      if (res?.url) {
        window.location.href = res.url;
        return;
      }
      throw new Error("Could not start checkout");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not start checkout");
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Start your 14-day trial"
      subtitle="Pick a plan and add your card · we won't charge for 14 days"
    >
      <form onSubmit={onSubmit} className="space-y-5">
        <ol className="flex items-center gap-2 text-xs text-muted-foreground">
          <li className="flex items-center gap-1.5 font-medium text-foreground">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">
              1
            </span>
            Plan &amp; card
          </li>
          <li className="h-px flex-1 bg-border" />
          <li className="flex items-center gap-1.5">
            <span className="flex h-5 w-5 items-center justify-center rounded-full border border-border text-[10px] font-semibold">
              2
            </span>
            Create login
          </li>
        </ol>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
          />
          <p className="text-xs text-muted-foreground">
            We'll use this for your account and your billing receipt.
          </p>
        </div>

        <div className="space-y-2">
          <Label>Billing</Label>
          <div className="grid grid-cols-2 rounded-lg border border-border bg-muted/30 p-1 text-sm">
            <button
              type="button"
              onClick={() => setInterval("monthly")}
              className={cn(
                "rounded-md py-1.5",
                interval === "monthly" ? "bg-background shadow-sm" : "text-muted-foreground",
              )}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setInterval("annual")}
              className={cn(
                "rounded-md py-1.5",
                interval === "annual" ? "bg-background shadow-sm" : "text-muted-foreground",
              )}
            >
              Annual <span className="ml-1 text-xs text-emerald-500">save ~30%</span>
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Plan</Label>
            <Link to="/pricing" className="text-xs text-primary hover:underline">
              Compare plans →
            </Link>
          </div>
          <div className="space-y-2">
            {PLAN_ORDER.map((key) => {
              const def = PLANS[key];
              const price = def.display[interval];
              const selected = plan === key;
              return (
                <button
                  type="button"
                  key={key}
                  onClick={() => setPlan(key)}
                  className={cn(
                    "w-full rounded-lg border p-3 text-left transition",
                    selected
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "border-border hover:border-primary/50",
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="font-medium">{def.name}</div>
                      {def.popular && (
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                          MOST POPULAR
                        </span>
                      )}
                    </div>
                    <div className="text-sm font-semibold">
                      ${price.amount}
                      <span className="text-xs font-normal text-muted-foreground">
                        {price.suffix}
                      </span>
                    </div>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{def.tagline}</div>
                  {selected && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-primary">
                      <Check className="h-3 w-3" /> 14-day free trial included
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <Button type="submit" className="w-full" disabled={submitting}>
          <CreditCard className="mr-2 h-4 w-4" />
          {submitting ? "Starting checkout…" : "Continue to payment"}
        </Button>
        <p className="text-center text-xs text-muted-foreground">
          You'll add a card next. No charge for 14 days, cancel anytime before then.
          You'll set your password right after.
        </p>
        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-foreground hover:underline">
            Sign in
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
